import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeft, Check, CheckCircle2, Layers, Play, Printer } from 'lucide-react'
import { api, errorMessage, fieldErrors } from '@/lib/api'
import { cn } from '@/lib/cn'
import { formatDateTime } from '@/lib/date'
import { workedBy } from '@/lib/stageLog'
import { useAuth } from '@/context/AuthContext'
import type { ChecklistItem, DepartmentDeadline, Job, StageLog } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import Modal from '@/components/ui/Modal'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { Field, Input, Textarea } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import { StateBadge } from '@/components/ui/Badge'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'
import IssuePhotoPicker, { type IssuePhoto } from '@/components/job/IssuePhotoPicker'
import UpstreamSectionPicker, { type UpstreamSection } from '@/components/job/UpstreamSectionPicker'
import HelperPicker from '@/components/job/HelperPicker'
import UrgentBadge from '@/components/job/UrgentBadge'
import JobDateStrip from '@/components/job/JobDateStrip'
import JobOrderItems from '@/components/job/JobOrderItems'

export default function StaffJobDetail() {
  const { token } = useParams()
  const { user } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [issueOpen, setIssueOpen] = useState(false)
  // '' = nothing picked yet, 'other' = free text, otherwise a preset id.
  const [reasonId, setReasonId] = useState('')
  const [detail, setDetail] = useState('')
  const [photo, setPhoto] = useState<IssuePhoto | null>(null)
  // Previous sections told alongside the flagger's own colleagues. Set
  // when the issue dialog opens, cleared when it closes.
  const [notifySections, setNotifySections] = useState<number[]>([])
  const [confirm, setConfirm] = useState<'start' | 'partial' | 'complete' | null>(null)
  // Pieces typed into the part-done dialog, kept as text so the field can
  // be emptied while typing; cleared when the dialog closes.
  const [pieces, setPieces] = useState('')
  // Checklist item ids ticked in the complete dialog; cleared when it closes.
  const [ticked, setTicked] = useState<number[]>([])
  // Teammates credited on the completion; cleared when the dialog closes.
  const [helpers, setHelpers] = useState<number[]>([])
  // The open issue being settled from the History list, and its optional note.
  const [resolveTarget, setResolveTarget] = useState<StageLog | null>(null)
  const [resolveNote, setResolveNote] = useState('')

  const { data: payload, isLoading, error } = useQuery({
    queryKey: ['staff-job', token],
    queryFn: async () =>
      (
        await api.get<{
          data: Job
          department_deadlines?: DepartmentDeadline[]
          previous_departments?: UpstreamSection[]
        }>(`/staff/scan/${token}`)
      ).data,
    retry: false,
  })
  const job = payload?.data
  const deadlines = payload?.department_deadlines ?? []
  // Sections this job already passed through, in flow order. Served by the
  // API rather than sliced out of the deadlines, which are filtered to
  // sections holding a booked plan row and so can be a section short.
  const previousSections = payload?.previous_departments ?? []

  const reasons = useQuery({
    queryKey: ['staff-issue-reasons'],
    queryFn: async () => (await api.get<{ data: { id: number; name: string }[] }>('/staff/issue-reasons')).data.data,
  })

  // The section's checklist, each item asked by the Start or the Complete
  // dialog. None for a moment means that dialog is a plain confirm.
  const checklist = useQuery({
    queryKey: ['staff-checklist-items'],
    queryFn: async () =>
      (await api.get<{ data: Pick<ChecklistItem, 'id' | 'text' | 'stage'>[] }>('/staff/checklist-items')).data.data,
  })

  const stage = useMutation({
    mutationFn: async (payload: {
      action: 'start' | 'partial' | 'complete' | 'issue'
      pieces?: number
      issue_reason_id?: number | null
      reason?: string
      checklist_item_ids?: number[]
      helper_ids?: number[]
      notify_department_ids?: number[]
      photo_path?: string
      photo_key?: string
    }) =>
      (await api.post(`/staff/jobs/${job!.id}/stage`, payload)).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['staff-job', token] })
      queryClient.invalidateQueries({ queryKey: ['staff-jobs'] })
      toast.success(data.message ?? 'Stage updated')
      closeIssue()
      closeConfirm()
    },
    onError: (err) => {
      toast.error('Update failed', errorMessage(err))
      const errorKeys = Object.keys(fieldErrors(err))
      // A teammate was suspended after the list loaded - fetch it again.
      if (errorKeys.some((key) => key.startsWith('helper_ids'))) {
        queryClient.invalidateQueries({ queryKey: ['staff-colleagues'] })
      }
      // The job moved on, so the sections behind it are not what the
      // picker was offering - fetch the job again.
      if (errorKeys.some((key) => key.startsWith('notify_department_ids'))) {
        queryClient.invalidateQueries({ queryKey: ['staff-job', token] })
      }
      // The admin changed the checklist, or the job was never started here.
      if (errorKeys.includes('checklist')) {
        queryClient.invalidateQueries({ queryKey: ['staff-checklist-items'] })
        queryClient.invalidateQueries({ queryKey: ['staff-job', token] })
      }
      closeConfirm()
    },
  })

  // Settling the open issue from the History list. Separate from `stage`: it
  // is its own endpoint and its own dialog, and the server holds the rule.
  const resolve = useMutation({
    mutationFn: async ({ id, note }: { id: number; note: string }) =>
      (await api.post<{ message: string }>(`/staff/issues/${id}/resolve`, { note: note.trim() || undefined })).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['staff-job', token] })
      queryClient.invalidateQueries({ queryKey: ['staff-jobs'] })
      toast.success(data.message ?? 'Issue resolved')
      closeResolve()
    },
    onError: (err) => {
      toast.error('Resolve failed', errorMessage(err))
      // A 403/422 means the record or the viewer moved on. Redraw so a stale
      // Resolve button disappears; the note stays for a retry.
      queryClient.invalidateQueries({ queryKey: ['staff-job', token] })
    },
  })

  const openIssue = () => {
    // The section right before this one is the usual suspect, so it starts
    // ticked. Set on open rather than in an effect, which would fight an
    // operator who deliberately clears it.
    const immediate = previousSections.at(-1)
    setNotifySections(immediate ? [immediate.id] : [])
    setIssueOpen(true)
  }

  const closeIssue = () => {
    setIssueOpen(false)
    setReasonId('')
    setDetail('')
    setPhoto(null)
    setNotifySections([])
  }

  const closeConfirm = () => {
    setConfirm(null)
    setTicked([])
    setHelpers([])
    setPieces('')
  }

  const closeResolve = () => {
    setResolveTarget(null)
    setResolveNote('')
  }

  const allItems = checklist.data ?? []
  // An item with no stage came from an API older than the choice, where every
  // item was asked on complete.
  const startItems = allItems.filter((item) => item.stage === 'start')
  const completeItems = allItems.filter((item) => item.stage !== 'start')
  const canStart = startItems.every((item) => ticked.includes(item.id))
  const canComplete = completeItems.every((item) => ticked.includes(item.id))
  const toggleItem = (id: number) =>
    setTicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  const presets = reasons.data ?? []
  // No presets configured: the modal is just the free-text field, as before.
  const isOther = reasonId === 'other' || presets.length === 0
  const canFlag = (reasonId !== '' || presets.length === 0) && (!isOther || detail.trim() !== '')

  // Named in flow order, whatever order they were tapped in.
  const chosenNames = previousSections.filter((s) => notifySections.includes(s.id)).map((s) => s.name)

  const flagIssue = () =>
    stage.mutate({
      action: 'issue',
      issue_reason_id: isOther ? null : Number(reasonId),
      reason: detail.trim() || undefined,
      // Always sent, even empty: an explicit [] is "tell nobody upstream".
      notify_department_ids: notifySections,
      ...(photo ?? {}),
    })

  if (isLoading) return <LogoLoader />

  if (error || !job) {
    return (
      <Card className="mx-auto max-w-md text-center">
        <p className="font-bold">Job not found</p>
        <p className="mt-1 text-sm text-muted">Check the job code and try again.</p>
        <Link to="/staff">
          <Button className="mt-4">Back to scan</Button>
        </Link>
      </Card>
    )
  }

  const atMySection = job.current_department?.id === user?.department?.id
  const started = job.stage_logs?.some(
    (log) => log.department === user?.department?.name && log.action === 'start',
  )
  const order = job.order

  // department_deadlines come in flow order, so the entry after ours is the
  // section the job moves to on complete.
  const sectionIndex = deadlines.findIndex((d) => d.department === job.current_department?.name)
  // The section's own booked row: what it owes and what is already out. A job
  // booked before section planning has no row, so part-done is not offered.
  const myPlan = sectionIndex >= 0 ? deadlines[sectionIndex] : undefined
  const planPieces = myPlan?.pieces ?? null
  const planDone = myPlan?.pieces_done ?? 0
  const remaining = planPieces === null ? null : planPieces - planDone
  // A part-done line needs at least one piece left over, or it is a completion.
  const canPartial = remaining !== null && remaining > 1
  const piecesNumber = Number(pieces)
  const piecesValid =
    pieces.trim() !== '' &&
    Number.isInteger(piecesNumber) &&
    piecesNumber >= 1 &&
    remaining !== null &&
    piecesNumber < remaining
  const nextSection = sectionIndex >= 0 ? deadlines[sectionIndex + 1]?.department : undefined
  const isLastSection = sectionIndex >= 0 && sectionIndex === deadlines.length - 1

  // In the header from sm, on the Order details card below it.
  const sheetButton = (
    <Link to={`/staff/jobs/${job.id}/sheet`} target="_blank" rel="noreferrer">
      <Button variant="outline" className="max-sm:h-7 max-sm:gap-1.5 max-sm:px-2.5 max-sm:text-xs">
        <Printer className="size-4 max-sm:size-3.5" /> Sheet
      </Button>
    </Link>
  )

  return (
    <div>
      <Link to="/staff" className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-brand-600 hover:underline">
        <ArrowLeft className="size-4" /> Back
      </Link>
      <PageHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1 sm:gap-2">
            {/* 16px below sm so the code, URGENT and the status share one line at 360px. */}
            <span className="font-mono text-base sm:text-lg">{job.code}</span>
            <UrgentBadge urgent={order?.is_urgent} />
            {/* Phones: the status rides beside the code instead of on its own row. */}
            <span className="flex sm:hidden">
              <StateBadge state={job.status} />
            </span>
          </span>
        }
        subtitle={`Order ${order?.number} - ${order?.agent?.name ?? ''}`}
        hideActionsOnMobile
        actions={
          <>
            <StateBadge state={job.status} />
            {sheetButton}
          </>
        }
      />

      <JobDateStrip job={job} deadlines={deadlines} />

      <div className="grid gap-4 lg:grid-cols-2">
        {/* The compact 28px phone button overhangs the heading line by 2px each side rather than making the row taller. */}
        <JobOrderItems order={order} action={<div className="-my-0.5 sm:hidden">{sheetButton}</div>} />

        <Card>
          <SectionLabel>
            Update stage - {job.current_department?.name ?? (job.status === 'cancelled' ? 'cancelled' : 'completed')}
          </SectionLabel>
          {job.status === 'cancelled' ? (
            <p className="rounded-lg border border-danger/60 bg-danger/20 p-3 text-sm font-semibold text-danger">
              This job was cancelled. Stop work on it and return the job sheet to the office.
            </p>
          ) : job.warehouse ? (
            <p className="text-sm text-muted">
              This job is with external warehouse partner <span className="font-bold text-ink">{job.warehouse.name}</span>.
              An admin must take it back before your section can work on it.
            </p>
          ) : job.awaiting_proceed ? (
            <p className="rounded-lg border border-warning/60 bg-warning/20 p-3 text-sm font-semibold text-warning">
              This job is still pending in the office. It reaches {job.current_department?.name ?? 'the floor'} once the
              office proceeds it.
            </p>
          ) : !atMySection ? (
            <p className="text-sm text-muted">
              This job is currently at <span className="font-bold text-ink">{job.current_department?.name ?? 'done'}</span>,
              not your section ({user?.department?.name}).
            </p>
          ) : (
            <div className="grid gap-2">
              <Button
                disabled={started || stage.isPending}
                loading={stage.isPending && stage.variables?.action === 'start'}
                onClick={() => setConfirm('start')}
              >
                <Play className="size-4" /> Start work
              </Button>
              {canPartial && (
                <Button
                  variant="outline"
                  disabled={!started || stage.isPending}
                  loading={stage.isPending && stage.variables?.action === 'partial'}
                  onClick={() => setConfirm('partial')}
                >
                  <Layers className="size-4" /> Part done - record pieces finished
                </Button>
              )}
              <Button
                variant="outline"
                disabled={!started || stage.isPending}
                loading={stage.isPending && stage.variables?.action === 'complete'}
                onClick={() => setConfirm('complete')}
                className="border-success/60 bg-success/20 text-success hover:bg-success/5"
              >
                <CheckCircle2 className="size-4" /> Complete - pass to next section
              </Button>
              <Button variant="danger" disabled={stage.isPending} onClick={openIssue}>
                <AlertTriangle className="size-4" /> Flag issue
              </Button>
            </div>
          )}

          <SectionLabel className="mt-6">History</SectionLabel>
          <ol className="space-y-2 text-xs">
            {job.stage_logs?.map((log) => (
              <li key={log.id} className="rounded-lg bg-app px-3 py-2">
                <div className="flex items-start justify-between gap-2">
                  <span className="min-w-0 font-semibold">
                    {log.department} - {log.action === 'partial' ? 'part done' : log.action}
                    {/* Pieces are what a part-done line is for, and they say what
                        a completion actually closed. Older lines carry none. */}
                    {typeof log.pieces === 'number' && log.pieces > 0 && (
                      <span className="ml-1 font-normal text-muted">{log.pieces} pcs</span>
                    )}
                    {log.staff && <span className="block font-normal text-muted">by {workedBy(log)}</span>}
                    {log.reason && <span className="block font-normal text-status-danger">{log.reason}</span>}
                    {(log.notified_departments?.length ?? 0) > 0 && (
                      <span className="block font-normal text-muted">
                        Also told {log.notified_departments!.map((d) => d.name).join(', ')}
                      </span>
                    )}
                    {log.photo_path && (
                      <a href={log.photo_path} target="_blank" rel="noreferrer" className="mt-1 block w-fit">
                        <img
                          src={log.photo_path}
                          alt="Photo of the problem"
                          className="size-16 rounded-lg border border-foreground/15 object-cover"
                        />
                      </a>
                    )}
                    {(log.checklist?.length ?? 0) > 0 && (
                      <span className="mt-0.5 block space-y-0.5 font-normal text-status-success">
                        {log.checklist!.map((item) => (
                          <span key={item.id} className="flex items-start gap-1">
                            <Check className="mt-0.5 size-3 shrink-0" /> {item.text}
                          </span>
                        ))}
                      </span>
                    )}
                    {log.resolved_at && (
                      <span className="block font-normal text-status-success">
                        Resolved by {log.resolved_by ?? 'admin'} on {formatDateTime(log.resolved_at)}
                        {log.resolution_note ? ` - ${log.resolution_note}` : ''}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-muted">{formatDateTime(log.at)}</span>
                </div>
                {log.can_resolve && (
                  <div className="mt-2 flex justify-end sm:justify-start">
                    <Button
                      variant="outline"
                      className="max-sm:h-8 max-sm:gap-1.5 max-sm:px-2.5 max-sm:text-xs"
                      onClick={() => {
                        setResolveTarget(log)
                        setResolveNote('')
                      }}
                    >
                      <CheckCircle2 className="size-4" /> Resolve issue
                    </Button>
                  </div>
                )}
              </li>
            ))}
            {(job.stage_logs?.length ?? 0) === 0 && <p className="text-muted">No scans yet.</p>}
          </ol>
        </Card>
      </div>

      <ConfirmDialog
        open={confirm === 'start'}
        onClose={closeConfirm}
        onConfirm={() => stage.mutate({ action: 'start', checklist_item_ids: ticked })}
        title={`Start work on ${job.code}?`}
        confirmLabel="Start work"
        loading={stage.isPending}
        confirmDisabled={!canStart}
      >
        <p>This logs the start time for {job.current_department?.name ?? 'your section'} and moves the job to in progress.</p>
        {startItems.length > 0 && (
          <ChecklistBoxes
            items={startItems}
            ticked={ticked}
            onToggle={toggleItem}
            heading="Confirm before starting"
            footnote="Tick every item to confirm the job is ready to start."
          />
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm === 'partial'}
        onClose={closeConfirm}
        onConfirm={() => stage.mutate({ action: 'partial', pieces: piecesNumber })}
        title={`Record part-done pieces on ${job.code}?`}
        confirmLabel="Record"
        loading={stage.isPending}
        confirmDisabled={!piecesValid}
      >
        <p>
          {job.current_department?.name ?? 'Your section'} keeps this job. The balance is booked onto its next
          working day and sits at the top of the section's list until it is finished.
        </p>
        <Field label="Pieces finished" htmlFor="part-done-pieces" className="mt-3">
          <Input
            id="part-done-pieces"
            type="number"
            inputMode="numeric"
            min={1}
            max={remaining === null ? undefined : remaining - 1}
            value={pieces}
            onChange={(e) => setPieces(e.target.value)}
            disabled={stage.isPending}
            placeholder={remaining === null ? '' : `1 - ${remaining - 1}`}
          />
        </Field>
        <p className="mt-1.5 text-xs text-muted">
          {planDone > 0
            ? `${planDone} of ${planPieces} pcs already finished here, ${remaining} left.`
            : `${planPieces} pcs booked at this section.`}{' '}
          Finished them all? Use Complete instead.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm === 'complete'}
        onClose={closeConfirm}
        onConfirm={() => stage.mutate({ action: 'complete', checklist_item_ids: ticked, helper_ids: helpers })}
        title={`Mark ${job.code} complete?`}
        confirmLabel="Complete"
        loading={stage.isPending}
        confirmDisabled={!canComplete}
      >
        <p>
          {nextSection
            ? `${job.current_department?.name ?? 'Your section'} is done and the job passes to ${nextSection}.`
            : isLastSection
              ? 'This is the last section - the job and the order will be marked completed.'
              : 'The job passes to the next section.'}
        </p>
        {planDone > 0 && (
          <p className="mt-1.5 text-xs text-muted">
            This closes the remaining {remaining} of {planPieces} pcs at this section.
          </p>
        )}
        <div className="mt-3">
          <HelperPicker value={helpers} onChange={setHelpers} disabled={stage.isPending} enabled={confirm === 'complete'} />
        </div>
        {completeItems.length > 0 && (
          <ChecklistBoxes
            items={completeItems}
            ticked={ticked}
            onToggle={toggleItem}
            heading="Confirm before completing"
            footnote="Tick every item to confirm this section's work is done."
          />
        )}
      </ConfirmDialog>

      <Modal
        open={issueOpen}
        onClose={closeIssue}
        title="Flag production issue"
        footer={
          <>
            <Button variant="outline" onClick={closeIssue}>
              Cancel
            </Button>
            <Button variant="danger" loading={stage.isPending} disabled={!canFlag} onClick={flagIssue}>
              Flag issue
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {presets.length > 0 && (
            <Field label="Reason">
              <Select
                value={reasonId}
                onChange={(value) => {
                  setReasonId(value)
                  if (value !== 'other') setDetail('')
                }}
                placeholder="Choose a reason"
                options={[...presets.map((r) => ({ value: String(r.id), label: r.name })), { value: 'other', label: 'Other' }]}
              />
            </Field>
          )}
          {isOther ? (
            <Field label="Describe the issue">
              <Textarea rows={3} value={detail} onChange={(e) => setDetail(e.target.value)} maxLength={500} placeholder="e.g. artwork resolution too low" />
            </Field>
          ) : (
            reasonId !== '' && (
              <Field label="Extra details (optional)">
                <Textarea rows={3} value={detail} onChange={(e) => setDetail(e.target.value)} maxLength={500} placeholder="e.g. navy came out black on the left sleeve" />
              </Field>
            )
          )}
          <Field label="Photo of the problem (optional)">
            <IssuePhotoPicker endpoint="/staff/issue-photo" value={photo} onChange={setPhoto} disabled={stage.isPending} />
          </Field>
          <UpstreamSectionPicker
            sections={previousSections}
            value={notifySections}
            onChange={setNotifySections}
            disabled={stage.isPending}
          />
        </div>
        <p className="mt-3 text-xs text-muted">
          Your section colleagues and the admins are always notified
          {chosenNames.length > 0 && `, and so ${chosenNames.length > 1 ? 'are' : 'is'} ${listOf(chosenNames)}`}. The
          order is marked as issue until it is resolved.
        </p>
      </Modal>

      <ConfirmDialog
        open={resolveTarget !== null}
        onClose={closeResolve}
        onConfirm={() => resolveTarget && resolve.mutate({ id: resolveTarget.id, note: resolveNote })}
        title="Mark this issue resolved?"
        confirmLabel="Mark resolved"
        loading={resolve.isPending}
      >
        <div className="space-y-3">
          <p>
            This returns the job to {job.current_department?.name ?? 'its section'} so the floor can carry on.
          </p>
          <Field label="Resolution note (optional)">
            <Textarea
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="What was decided, e.g. the previous section replaced the fabric"
            />
          </Field>
        </div>
      </ConfirmDialog>
    </div>
  )
}

/** "Design", "Design and Print", "Design, Print and Laser". */
function listOf(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** The boxes a Start or Complete dialog asks staff to tick, in admin order. */
function ChecklistBoxes({
  items,
  ticked,
  onToggle,
  heading,
  footnote,
}: {
  items: { id: number; text: string }[]
  ticked: number[]
  onToggle: (id: number) => void
  heading: string
  footnote: string
}) {
  return (
    <div className="mt-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-foreground/60">{heading}</p>
      <div className="mt-1 divide-y divide-foreground/10 rounded-lg border border-foreground/10">
        {items.map((item) => {
          const on = ticked.includes(item.id)
          return (
            <label key={item.id} className="flex cursor-pointer items-start gap-3 px-3 py-2.5">
              <input
                type="checkbox"
                checked={on}
                onChange={() => onToggle(item.id)}
                className="mt-0.5 size-4 shrink-0 accent-primary"
              />
              <span className={cn('text-sm', on ? 'font-semibold text-foreground' : 'text-foreground/80')}>{item.text}</span>
            </label>
          )
        })}
      </div>
      <p className="mt-2 text-xs text-muted">{footnote}</p>
    </div>
  )
}
